/**
 * MarkerFactory: build an in-world marker entity for a prop's `MarkerStyle`.
 *
 * This is the single extension point for "模型样式": `primitive` shapes are
 * rendered today; `gsplat` / `glb` / `sprite` (2D UI billboard) styles are
 * stubbed with a translucent fallback + console hint so new styles can be added
 * here without touching the editor or managers.
 */

import { BLEND_NORMAL, CULLFACE_NONE, Entity, StandardMaterial, Asset } from 'playcanvas';
import type { Application, Texture } from 'playcanvas';
import type { MarkerStyle } from './propTypes';
import type { MarkerConfig } from './SceneObjectTypes';
import { getLayers, worldIds } from '../render/LayerManager';
import { isMobileRuntime } from '../app/createApp';
import { mobileDegradeActive } from '../app/stabilityGuard';

/** Overlay layer id for billboard-like markers (null before createApp initializes layers). */
function overlayLayerId(): number[] {
    const layers = getLayers();
    return layers ? [layers.overlay.id] : [];
}

/** Put every render/gsplat component on `entity` (and descendants) on the
 *  Overlay layer for billboard-like marker styles that intentionally render
 *  above the scene. */
function assignOverlayLayers(entity: Entity): void {
    const ids = overlayLayerId();
    if (!ids.length) return;
    entity.forEach((node) => {
        const e = node as Entity;
        if (e.render) e.render.layers = ids;
        if (e.model) e.model.layers = ids;
        if (e.gsplat) e.gsplat.layers = ids as number[];
    });
}

/** Put mesh-based custom appearances on the custom World layer. Unlike the
 *  transparent-only Overlay layer, World has an opaque render pass, so opaque
 *  GLB materials actually draw after the placeholder is removed. */
function assignWorldLayers(entity: Entity): void {
    const ids = worldIds();
    if (!ids.length) return;
    entity.forEach((node) => {
        const e = node as Entity;
        if (e.render) e.render.layers = ids;
        if (e.model) e.model.layers = ids;
    });
}

/** Shared depth-only material (no color writes) for the GLB marker occluder
 *  twin. Same recipe as AvatarController's remote-avatar occluder material. */
let _markerDepthMaterial: StandardMaterial | null = null;
function markerDepthMaterial(): StandardMaterial {
    if (!_markerDepthMaterial) {
        const mat = new StandardMaterial();
        mat.name = 'MarkerAvatarDepthOccluder';
        mat.diffuse.set(0, 0, 0);
        mat.emissive.set(0, 0, 0);
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.useFog = false;
        mat.useTonemap = false;
        // 不写任何颜色：只在 AvatarDepth 层写入深度，供本地玩家 avatar 遮挡测试。
        mat.redWrite = false;
        mat.greenWrite = false;
        mat.blueWrite = false;
        mat.alphaWrite = false;
        mat.depthWrite = true;
        mat.depthTest = true;
        // 双面都写深度：与可见模型一致的遮挡体积（叶片/裙摆类面片双面渲染）。
        mat.cull = CULLFACE_NONE;
        mat.update();
        _markerDepthMaterial = mat;
    }
    return _markerDepthMaterial;
}

/** 让摆放的 GLB 物体能遮挡「本地玩家」的第三人称模型：克隆一份 depth-only
 *  双生体挂到 AvatarDepth 层。AvatarDepth 在绘制本地 avatar 前会先清空场景
 *  深度（高斯泼溅不写深度、碰撞代理网格会过填充穿出真实表面，会把贴墙的本
 *  地人物错误裁剪——见 LayerManager），随后只补写「允许遮挡本地人物」的深度：
 *  远程玩家的真实网格经 enableDepthOccluder 补写，摆放的 GLB 是精确网格，
 *  同样应遮挡本地人物（修复：此前 marker 在清空前写的深度被一并清掉，本地
 *  人物永远穿在 GLB 前面）。双生体是 marker 的子实体，随实体显隐/销毁走
 *  render 组件生命周期，无需手动清理；透明材质的 GLB 也按整体遮挡处理。 */
function attachAvatarDepthTwin(parent: Entity, model: Entity): void {
    const layers = getLayers();
    if (!layers) return;
    const twin = model.clone();
    twin.name = 'marker-avatar-depth';
    const ids = [layers.avatarDepth.id];
    twin.forEach((node) => {
        const e = node as Entity;
        const meshInstances =
            (e.render as unknown as { meshInstances?: { material: unknown; castShadow?: boolean }[] } | undefined)?.meshInstances
            ?? (e.model as unknown as { meshInstances?: { material: unknown; castShadow?: boolean }[] } | undefined)?.meshInstances;
        if (e.render) e.render.layers = ids;
        if (e.model) e.model.layers = ids;
        if (meshInstances) {
            for (const mi of meshInstances) {
                mi.material = markerDepthMaterial();
                mi.castShadow = false;
            }
        }
    });
    parent.addChild(twin);
}

/** GLB previews are placed inside a mostly-unlit gaussian scene with no HDR
 *  reflection atlas. Full-metal PBR materials would otherwise render close to
 *  black even when their baseColor is visible in DCC/GLB preview tools. Clone
 *  per-instance materials so the source asset remains unchanged. */
/** Tone down skybox reflections on GLB materials so the model isn't a mirror.
 *  Everything else (diffuse, emissive, metalness) stays as authored in the GLB. */
function adaptGlbMarkerMaterials(entity: Entity): void {
    const clones = new Map<StandardMaterial, StandardMaterial>();
    entity.forEach((node) => {
        const e = node as Entity;
        const meshInstances = (e.render as unknown as { meshInstances?: { material: unknown }[] } | undefined)?.meshInstances;
        if (!meshInstances) return;
        for (const mi of meshInstances) {
            const mat = mi.material as StandardMaterial | undefined;
            if (!(mat instanceof StandardMaterial)) continue;
            let copy = clones.get(mat);
            if (!copy) {
                copy = mat.clone() as StandardMaterial;
                copy.useSkybox = false;
                copy.useFog = false;
                copy.reflectivity = 0.25;
                copy.metalness = Math.min(copy.metalness, 0.25);
                copy.emissive.copy(copy.diffuse);
                copy.emissiveIntensity = Math.max(copy.emissiveIntensity, 0.18);
                copy.update();
                clones.set(mat, copy);
            }
            mi.material = copy;
        }
    });
}

/** Normalize a scene.json MarkerConfig (optional fields) into a MarkerStyle
 *  with defaults, then build the marker entity. */
export function createMarkerFromConfig(app: Application, cfg: MarkerConfig): Entity {
    let style: MarkerStyle;
    switch (cfg.kind) {
        case 'primitive':
            style = {
                kind: 'primitive',
                shape: cfg.shape,
                color: cfg.color ?? [1, 1, 1],
                scale: cfg.scale ?? [1, 1, 1],
                opacity: cfg.opacity ?? 1
            };
            break;
        case 'video':
            style = { kind: 'video', scale: cfg.scale ?? [4, 2.25, 1] };
            break;
        case 'imagePanel':
            style = {
                kind: 'imagePanel',
                url: cfg.url,
                scale: cfg.scale ?? [4, 2.25, 1],
                autoRotate: cfg.autoRotate,
                rotationSpeed: cfg.rotationSpeed,
                rotationAxis: cfg.rotationAxis
            };
            break;
        case 'glb':
            style = { kind: 'glb', url: cfg.url, scale: cfg.scale ?? 1 };
            break;
        case 'gsplat':
            style = { kind: 'gsplat', url: cfg.url, scale: cfg.scale ?? 1 };
            break;
        case 'sprite':
            style = { kind: 'sprite', url: cfg.url, scale: cfg.scale ?? 1 };
            break;
    }
    return createMarker(app, style);
}

/** Build (but do not parent) a marker entity for the given style. */
export function createMarker(app: Application, style: MarkerStyle): Entity {
    const e = new Entity('marker');

    switch (style.kind) {
        case 'primitive':
            e.addComponent('render', { type: style.shape });
            e.render!.material = makeEmissiveMaterial(style.color, style.opacity ?? 1);
            e.setLocalScale(style.scale[0], style.scale[1], style.scale[2]);
            assignWorldLayers(e);
            break;

        case 'gsplat':
            // Show a translucent placeholder sphere, then swap in the real
            // gaussian splat once the gsplat asset loads. The splat lives on a
            // child entity with the 180° Z flip (splat→world space, same
            // convention as loadGaussianScene); the parent carries pos/rot/scale.
            e.addComponent('render', { type: 'sphere' });
            e.render!.material = makeEmissiveMaterial([1, 0.3, 0.8], 0.5);
            e.setLocalScale(style.scale, style.scale, style.scale);
            loadGsplatMarker(app, e, style.url);
            break;

        case 'glb':
            // Show a translucent placeholder box immediately, then swap in the
            // real GLB model once the container asset loads. The placeholder is
            // parented to `e` (which carries the configured scale), so the model
            // inherits that scale.
            e.addComponent('render', { type: 'box' });
            e.render!.material = makeEmissiveMaterial([0.6, 0.8, 1], 0.5);
            e.setLocalScale(style.scale, style.scale, style.scale);
            loadGlbMarker(app, e, style.url);
            assignWorldLayers(e);
            break;

        case 'video':
            e.addComponent('render', { type: 'plane' });
            e.render!.material = makeVideoPlaceholderMaterial();
            e.setLocalScale(style.scale[0], style.scale[1], style.scale[2]);
            assignWorldLayers(e);
            break;

        case 'imagePanel':
            e.addComponent('render', { type: 'plane' });
            e.render!.material = makeImagePanelPlaceholderMaterial();
            e.setLocalScale(style.scale[0], style.scale[1], style.scale[2]);
            loadImagePanelMarker(app, e, style.url);
            assignWorldLayers(e);
            break;

        case 'sprite':
            e.addComponent('render', { type: 'plane' });
            e.render!.material = makeEmissiveMaterial([1, 0.9, 0.4], 0.35);
            e.setLocalScale(style.scale, style.scale, style.scale);
            loadSpriteMarker(app, e, style.url);
            break;
    }

    // Gaussian/sprite marker styles stay billboard-like overlays. Mesh-based
    // markers assign themselves to World so the scene depth can occlude them.
    if (style.kind === 'gsplat' || style.kind === 'sprite') assignOverlayLayers(e);
    return e;
}

/** Load an image-backed world panel, intended for portal appearances. */
function loadImagePanelMarker(app: Application, parent: Entity, url: string): void {
    if (!url.trim()) return;
    const existing = app.assets.getByUrl(url);
    const asset = existing ?? new Asset(url, 'texture', { url });
    if (!existing) app.assets.add(asset);

    const apply = () => {
        const texture = asset.resource as Texture | undefined;
        if (!texture) return;
        if (!parent.render) parent.addComponent('render', { type: 'plane' });
        const mat = new StandardMaterial();
        mat.diffuse.set(1, 1, 1);
        mat.emissive.set(1, 1, 1);
        mat.emissiveIntensity = 1.15;
        mat.diffuseMap = texture;
        mat.emissiveMap = texture;
        mat.opacityMap = texture;
        mat.blendType = BLEND_NORMAL;
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.useFog = false;
        mat.cull = CULLFACE_NONE;
        mat.depthTest = true;
        mat.depthWrite = false;
        mat.update();
        parent.render!.material = mat;
        assignWorldLayers(parent);
    };

    if (asset.loaded) {
        apply();
    } else {
        asset.on('load', apply);
        asset.on('error', (err: string) => {
            console.warn(
                `[markerFactory] imagePanel 图片加载失败: ${url}\n` +
                `  常见原因：1) 地址错误/404  2) 图片跨域(CORS)  3) 图片格式不受当前浏览器支持。原始错误：${err}`
            );
            // 失败态不黑屏：占位虚影换成淡橙暖色，与「加载中」的淡蓝区分，
            // 现场一眼能看出这扇门的图没配对。
            const mat = parent.render?.material;
            if (mat instanceof StandardMaterial) {
                mat.emissive.set(1, 0.55, 0.2);
                mat.update();
            }
        });
        if (!existing) app.assets.load(asset);
    }
}

/** Load a sprite image as a texture-backed plane. */
function loadSpriteMarker(app: Application, parent: Entity, url: string): void {
    const existing = app.assets.getByUrl(url);
    const asset = existing ?? new Asset(url, 'texture', { url });
    if (!existing) app.assets.add(asset);

    const apply = () => {
        const texture = asset.resource as Texture | undefined;
        if (!texture) return;
        if (!parent.render) parent.addComponent('render', { type: 'plane' });
        const mat = new StandardMaterial();
        mat.diffuse.set(1, 1, 1);
        mat.emissive.set(1, 1, 1);
        mat.emissiveIntensity = 1;
        mat.diffuseMap = texture;
        mat.emissiveMap = texture;
        mat.useLighting = false;
        mat.useSkybox = false;
        mat.useFog = false;
        mat.cull = CULLFACE_NONE;
        mat.depthTest = true;
        mat.depthWrite = false;
        mat.blendType = BLEND_NORMAL;
        mat.update();
        parent.render!.material = mat;
        assignOverlayLayers(parent);
    };

    if (asset.loaded) {
        apply();
    } else {
        asset.on('load', apply);
        asset.on('error', (err: string) => console.warn(
            `[markerFactory] sprite 图片加载失败: ${url}\n` +
            `  常见原因：1) 地址错误/404  2) 图片跨域(CORS)  3) 图片格式不受当前浏览器支持。原始错误：${err}`
        ));
        if (!existing) app.assets.load(asset);
    }
}

/** Load a GLB as a PlayCanvas container asset and parent the instantiated model
 *  under `parent` (replacing the placeholder render). The asset is cached by URL
 *  in the app's asset registry, so repeated marker rebuilds don't reload it. */
function loadGlbMarker(app: Application, parent: Entity, url: string): void {
    const existing = app.assets.getByUrl(url);
    const asset = existing ?? new Asset(url, 'container', { url });
    if (!existing) app.assets.add(asset);

    const apply = () => {
        // 宿主实体已被销毁（如放置预览在 GLB 加载完成前被取消）：GraphNode
        // .destroy() 会先 remove() 把 _parent 置空——迟到的 load 不再往死
        // 实体上挂模型（孤儿泄漏）。存活宿主必然已被父级 addChild。
        if (!parent.parent) return;
        const resource = asset.resource as
            | {
                  instantiateRenderEntity?: () => Entity;
                  instantiateModelEntity?: () => Entity;
              }
            | undefined;
        if (!resource) return;
        // Drop the placeholder box so only the model shows.
        if (parent.render) parent.removeComponent('render');
        let model: Entity | null = null;
        if (typeof resource.instantiateRenderEntity === 'function') {
            model = resource.instantiateRenderEntity();
        } else if (typeof resource.instantiateModelEntity === 'function') {
            model = resource.instantiateModelEntity();
        }
        if (model) {
            parent.addChild(model);
            adaptGlbMarkerMaterials(model);
        } else {
            console.warn(`[markerFactory] GLB 无可实例化模型: ${url}`);
        }
        assignWorldLayers(parent);
        // 双生体必须在 assignWorldLayers(parent) 之后挂：后者 forEach 会把所有
        // 子孙的层统一改成 World，先挂会被覆盖掉 AvatarDepth 层设置。
        // 降级档（stabilityGuard：boot 死亡信号或 mobileLowSpec 重载场景）手机
        // 端跳过：双生体是整棵克隆（网格+材质翻倍），手机资源峰值紧张时先保命，
        // 代价仅是本地人物穿 GLB 摆件（回到 2026-09-28 之前的手机端表现）。
        if (model && !(isMobileRuntime() && mobileDegradeActive())) attachAvatarDepthTwin(parent, model);
    };

    if (asset.loaded) {
        // 缓存命中时 apply 若同步执行，此刻实体尚在 createMarkerFromConfig
        // 内部、未被调用方 addChild —— 下方 !parent.parent 守卫会把换模整个
        // 吞掉（放置预览已加载过同 URL，正式物体就永远只剩半透明占位盒）。
        // 推迟到微任务：同步构造返回、调用方挂载完成后再换模；期间被销毁
        // （如取消放置）的宿主仍被守卫拦下。
        queueMicrotask(apply);
    } else {
        asset.on('load', apply);
        asset.on('error', (err: string) => console.warn(
            `[markerFactory] GLB 加载失败: ${url}\n` +
            `  常见原因：1) 地址错误/404  2) 跨域(CORS)：外链 GLB 服务器需返回 Access-Control-Allow-Origin  3) 页面是 https 但 GLB 是 http(混合内容被拦截)。原始错误：${err}`
        ));
        if (!existing) app.assets.load(asset);
    }
}

/** Load a gaussian splat (.sog / .lod-meta.json / .ply) as the marker visual.
 *  Mirrors `loadGaussianScene`: a gsplat component with `unified: true`, on a
 *  child entity rotated 180° about Z to map splat space into world space. The
 *  asset is cached by URL in the app's asset registry. */
function loadGsplatMarker(app: Application, parent: Entity, url: string): void {
    const existing = app.assets.getByUrl(url);
    const filename = url.split('/').pop() || 'splat';
    const asset = existing ?? new Asset(filename, 'gsplat', { url });
    if (!existing) app.assets.add(asset);

    const apply = () => {
        if (parent.render) parent.removeComponent('render');
        const child = new Entity('gsplat-marker');
        child.setLocalEulerAngles(0, 0, 180);
        child.addComponent('gsplat', { asset, unified: true });
        parent.addChild(child);
        assignOverlayLayers(parent);
    };

    if (asset.loaded) {
        apply();
    } else {
        asset.on('load', apply);
        asset.on('error', (err: string) => console.warn(
            `[markerFactory] 高斯模型加载失败: ${url}\n` +
            `  常见原因：1) 地址错误/404  2) 跨域(CORS)  3) 格式不支持(支持 .sog / .lod-meta.json / .ply)。原始错误：${err}`
        ));
        if (!existing) app.assets.load(asset);
    }
}

function makeEmissiveMaterial(color: [number, number, number], opacity: number): StandardMaterial {
    const mat = new StandardMaterial();
    mat.emissive.set(color[0], color[1], color[2]);
    mat.emissiveIntensity = 1.5;
    mat.diffuse.set(0, 0, 0);
    mat.useLighting = false;
    mat.useSkybox = false;
    mat.useFog = false;
    mat.depthTest = true;
    if (opacity < 1) {
        mat.blendType = BLEND_NORMAL;
        mat.opacity = opacity;
        mat.depthWrite = false;
    }
    mat.update();
    return mat;
}

function makeVideoPlaceholderMaterial(): StandardMaterial {
    const mat = new StandardMaterial();
    mat.diffuse.set(0.02, 0.02, 0.02);
    mat.emissive.set(0.02, 0.02, 0.02);
    mat.emissiveIntensity = 1;
    mat.useLighting = false;
    mat.useSkybox = false;
    mat.useFog = false;
    mat.cull = CULLFACE_NONE;
    mat.depthTest = true;
    mat.depthWrite = true;
    mat.update();
    return mat;
}

/** 图片面板占位材质：贴图加载中/加载失败时的「虚影门洞」——半透明淡蓝、
 *  不写深度（不黑屏、不遮挡身后场景），加载成功后由贴图材质整体替换。 */
function makeImagePanelPlaceholderMaterial(): StandardMaterial {
    const mat = new StandardMaterial();
    mat.diffuse.set(0, 0, 0);
    mat.emissive.set(0.45, 0.62, 0.85);
    mat.emissiveIntensity = 0.8;
    mat.opacity = 0.35;
    mat.blendType = BLEND_NORMAL;
    mat.useLighting = false;
    mat.useSkybox = false;
    mat.useFog = false;
    mat.cull = CULLFACE_NONE;
    mat.depthTest = true;
    mat.depthWrite = false;
    mat.update();
    return mat;
}
